if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.querySelector('.aj-gallery-nav__button--previous')?.addEventListener('click', () =>
          this.showAdjacentMedia(-1)
        );
        this.querySelector('.aj-gallery-nav__button--next')?.addEventListener('click', () =>
          this.showAdjacentMedia(1)
        );
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          // Thumbnail/desktop: slides swap in place — never jump the page.
          const desktopThumb =
            this.mql.matches && (this.dataset.desktopLayout || '').includes('thumbnail');
          if (desktopThumb) return;

          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft, behavior: 'smooth' });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Only scroll page if the media is fully off-screen.
          if (activeMediaRect.bottom > 0 && activeMediaRect.top < window.innerHeight) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      showAdjacentMedia(direction) {
        const media = Array.from(this.elements.viewer.querySelectorAll('.product__media-list > [data-media-id]')).filter(
          (item) => !item.classList.contains('product__media-item--variant') || !item.hidden
        );
        if (media.length < 2) return;

        let activeIndex = media.findIndex((item) => item.classList.contains('is-active'));
        if (activeIndex < 0 && this.elements.thumbnails) {
          const currentTarget = this.elements.thumbnails.querySelector('[aria-current="true"]')?.closest('[data-target]')
            ?.dataset.target;
          activeIndex = media.findIndex((item) => item.dataset.mediaId === currentTarget);
        }
        const nextIndex = (Math.max(activeIndex, 0) + direction + media.length) % media.length;
        this.setActiveMedia(media[nextIndex].dataset.mediaId, false);
        if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          media[nextIndex].firstElementChild?.animate(
            [
              { opacity: 0.25, transform: `translateX(${direction > 0 ? '7%' : '-7%'})` },
              { opacity: 1, transform: 'translateX(0)' },
            ],
            { duration: 480, easing: 'cubic-bezier(.22,1,.36,1)' }
          );
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        const slider = this.elements.thumbnails.slider;
        if (!slider) return;

        // Thumbs sit in a horizontal row below the main image.
        if (this.mql.matches && (this.dataset.desktopLayout || '').includes('thumbnail')) {
          if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
          slider.scrollTo({ left: thumbnail.offsetLeft, behavior: 'smooth' });
          return;
        }

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
        slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
